// mobile-shell-view — lógica PURA de qual view o MobileShell mostra. Paredes
// A3 (base) e A5 (overlay de detalhe + alvo de episódio no player).
//
// Separada do componente (mesmo padrão de onboarding-swipe.ts) pra ser testável
// sem DOM: o shell só liga estado de UI (useState + taps) nesta função. Assim
// "tab bar troca aba", "detalhe/busca têm prioridade" e "card → detalhe →
// player" são cobertos por teste de função pura, sem simular cliques.

export type MobileTab = 'home' | 'live' | 'movies' | 'series' | 'mylist'

/** Overlays temporários por cima do shell (não são abas). Ordem de prioridade
 *  na resolução = ordem desta união (player > detail > search > configureSource
 *  > profile). `player` (A4) e `detail` (A5) já são telas reais; busca segue
 *  placeholder (A6), configurar fonte (A6) e perfil/ajustes (futuro) também.
 *  O `item` antigo foi substituído pelo `detail` (tap de card → DETALHE). */
export type MobileOverlay = 'player' | 'detail' | 'search' | 'configureSource' | 'profile'

/** Conteúdo aberto no player mobile (parede A4, estendido na A5 pra séries).
 *  Carregado no estado do shell quando o overlay é 'player'. title/poster vêm
 *  do card pra não "piscar"; resumeFrom vem da posição salva (continuar
 *  assistindo). Pra EPISÓDIO de série, `id` é o episodeId (signStream usa ele)
 *  e os campos series* carregam o contexto pra SALVAR a posição da série
 *  (series-episode) — o que ficou pendente na A4. */
export interface MobilePlayerTarget {
  id: string
  kind: 'vod' | 'series' | 'channel'
  title?: string | undefined
  poster?: string | null | undefined
  resumeFrom?: number | undefined
  /** Contexto de episódio (só quando kind==='series' e tocamos um ep). */
  seriesId?: string | undefined
  episodeId?: string | undefined
  seasonNumber?: number | undefined
  episodeNumber?: number | undefined
}

/** Conteúdo aberto na tela de DETALHE (parede A5). Só VOD e série têm detalhe
 *  (canal vai direto pro player). title/poster vêm do card pra a tela pintar
 *  imediato enquanto o getVodInfo/getSeriesInfo carrega. */
export interface MobileDetailTarget {
  id: string
  kind: 'vod' | 'series'
  title?: string | undefined
  poster?: string | null | undefined
}

/** Estado de navegação do shell (UI). */
export interface MobileShellState {
  tab: MobileTab
  /** Overlay ativo por cima da aba, ou null quando estamos numa aba. */
  overlay: MobileOverlay | null
  /** Conteúdo do player quando overlay === 'player'. null caso contrário. */
  player?: MobilePlayerTarget | null
  /** Conteúdo da tela de detalhe quando overlay === 'detail'. null caso
   *  contrário. Preservado ao abrir o player A PARTIR do detalhe, pra que o
   *  ← do player volte pro detalhe (e não pra aba). */
  detail?: MobileDetailTarget | null
  /** De onde o detalhe foi aberto (fix/busca-estado-perdido). Quando um
   *  card da BUSCA abre o detalhe, o voltar deve retornar PRA BUSCA (com o
   *  estado restaurado via store), não cair na Home. Sem isso o goBack fazia
   *  overlay:null e caía na aba ativa (Home). Só 'search' importa hoje;
   *  abertura por uma aba não precisa de marca (volta pra aba naturalmente). */
  detailFrom?: 'search' | null
}

/** A view efetiva a renderizar no conteúdo. Os overlays têm prioridade sobre a
 *  aba (são temporários por cima do shell). */
export type MobileView =
  | { kind: 'player'; target: MobilePlayerTarget }
  | { kind: 'detail'; target: MobileDetailTarget }
  | { kind: 'search' }
  | { kind: 'configureSource' }
  | { kind: 'profile' }
  | { kind: 'tab'; tab: MobileTab }

/** Resolve a view atual a partir do estado de navegação. */
export function resolveMobileView(state: MobileShellState): MobileView {
  // 'player' tem prioridade máxima (pode estar aberto POR CIMA do detalhe). Só
  // vale como view quando há um alvo carregado; sem alvo cai pro detalhe/aba.
  if (state.overlay === 'player' && state.player) {
    return { kind: 'player', target: state.player }
  }
  // 'detail' vale quando há alvo carregado; sem alvo cai pra aba (defensivo).
  if (state.overlay === 'detail') {
    return state.detail ? { kind: 'detail', target: state.detail } : { kind: 'tab', tab: state.tab }
  }
  if (state.overlay === 'player') {
    // player sem alvo — defensivo: cai pra aba.
    return { kind: 'tab', tab: state.tab }
  }
  if (state.overlay) return { kind: state.overlay }
  return { kind: 'tab', tab: state.tab }
}

/** A aba destacada na tab bar. Quando há um overlay aberto, NENHUMA aba fica
 *  ativa (estamos fora da navegação por abas), retorna null. */
export function activeTab(state: MobileShellState): MobileTab | null {
  if (state.overlay) return null
  return state.tab
}

/** Próximo estado ao "voltar" (botão ‹ da topbar / ← do player). PURO pra ser
 *  testável sem DOM (fix/busca-estado-perdido cobre o caso busca→detalhe→back).
 *
 * Hierarquia de volta:
 *   1. Player aberto SOBRE o detalhe → volta pro detalhe (mantém detail).
 *   2. Detalhe aberto A PARTIR da BUSCA → volta PRA BUSCA (não pra Home). A
 *      MobileSearch reidrata query/filtros do store ao remontar.
 *   3. Qualquer outro overlay → fecha e volta pra aba ativa.
 */
export function goBackState(state: MobileShellState): MobileShellState {
  if (state.overlay === 'player' && state.detail) {
    return { ...state, overlay: 'detail', player: null }
  }
  if (state.overlay === 'detail' && state.detailFrom === 'search') {
    // Volta pra busca; limpa o detalhe e a marca de origem.
    return { ...state, overlay: 'search', player: null, detail: null, detailFrom: null }
  }
  return { ...state, overlay: null, player: null, detail: null, detailFrom: null }
}

/** Estado inicial: aba Início, sem overlays. */
export const INITIAL_SHELL_STATE: MobileShellState = {
  tab: 'home',
  overlay: null,
  player: null,
  detail: null,
  detailFrom: null,
}
